import { NavLink, useParams } from 'react-router';
import { useT } from '@/i18n';
import { useProject, usePipelineSession, useSessions } from '@/lib/api/hooks';
import { useCrumb } from '@/store/crumb';
import type { Episode, ProjectDetail } from '@/lib/api/types';

/** Project, episode and the episode's latest pipeline session, from the route params. */
export function useEpisodeContext() {
  const { projectId, episodeId } = useParams();
  const project = useProject(projectId);
  const episode: Episode | undefined = project.data?.episodes.find((e) => e.id === episodeId);
  // Amendment 1: project detail rows carry latest_session_id; fall back to the sessions list on older APIs.
  const known = episode !== undefined && episode.latest_session_id !== undefined;
  const sessions = useSessions(episode && !known ? episodeId : undefined);
  const sessionId = known ? (episode!.latest_session_id ?? undefined) : sessions.data?.[0]?.id;
  const session = usePipelineSession(sessionId);
  const sessionsResolved = known || !!sessions.data;
  return { projectId: projectId!, episodeId: episodeId!, project, episode, sessions, sessionsResolved, sessionId, session };
}

/** Title block + tabs shared by the drill-down, upload and transcript screens. */
export function EpisodeHeader({ project, episode, actions }: { project: ProjectDetail; episode: Episode; actions?: React.ReactNode }) {
  const { t } = useT();
  useCrumb(`${project.title} · ${episode.number}`);
  const base = `/projects/${project.id}/episodes/${episode.id}`;
  const primary = project.kit?.effective.colours.primary ?? '#FF4A2E';
  return (
    <div className="stack" style={{ gap: 14 }}>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div className="stack">
          <span className="row muted small"><span className="swatch" style={{ background: primary }} />{project.title} · {project.customer.name}</span>
          <h1 className="h1">{episode.number} — {episode.title}</h1>
        </div>
        {actions ? <div className="row wrap">{actions}</div> : null}
      </div>
      <nav aria-label={t('nEpisode')} className="row wrap" style={{ gap: 6 }}>
        <NavLink end to={base} className="chip">{t('drilldown')}</NavLink>
        <NavLink to={`${base}/upload`} className="chip">{t('uploadTitle')}</NavLink>
        <NavLink to={`${base}/transcript`} className="chip">{t('transcriptTitle')}</NavLink>
      </nav>
    </div>
  );
}
